import { ApiError, classifyStatus, toApiError } from '../../api/errors';
import { API_V1, buildUrl } from '../../api/http';
import { asTransitions } from '../../api/normalise';
import type { Id, Transition } from '../../api/types';

/**
 * `POST /tickets/:id/transition`, KEEPING THE BODY OF A 409.
 *
 * 🔴 WHY THIS IS NOT `applyTransition` FROM `api/endpoints.ts`. It is the same endpoint with the same
 * body; the difference is what survives a refusal. The shared `request()` turns every non-2xx into an
 * `ApiError` carrying `message` and a single `reason` string — which is right for a list that failed
 * to load, and lossy here: the API's 409 also carries `from_status`, `to_status` and its `suggested`
 * next statuses (`services/transitions.ts`, `conflict(reason, { …, suggested, allowed })`).
 *
 * 🔴🔴 THIS WHOLE FILE STAYS, AND PH-19 DID NOT WEAKEN IT. Removing the gate from `drag-rules.ts`
 * removes ONE reason the API used to say no. It still says no, for reasons this client must not
 * duplicate and must not paraphrase:
 *   · `only an active member of this project may transition its tickets`  (403)
 *   · `'x' is a retired status and cannot be moved into`                  (409, reason status_inactive)
 *   · `ticket is already in status 'x'`                                   (409)
 *   · `'x' is not a status in this system`                                (400 — a typo, not a rule)
 * ⛔ Every one of those reaches the screen VERBATIM. A friendlier paraphrase would delete the only
 * part of the sentence that tells the reader what to do next.
 *
 * ⚠️ WHAT DID CHANGE IS ONE WORD. The list in the body is `suggested`, and `allowed` is the API's
 * own deprecated alias for it. `suggested` is read first; `allowed` is still accepted so a board
 * pointed at an older deployment keeps its refusal notice. ⛔ The list is no longer used to narrow
 * what the board will accept — see `adoptRefusal` in `drag-rules.ts` for why that was the bug.
 *
 * ⛔ Everything that is NOT a 409 is thrown as the ordinary `ApiError`, so `ErrorNotice` renders a
 * 401, a 503 and a network failure exactly as every other screen does. This function does not invent
 * a second error vocabulary.
 */

export interface TransitionRefusal {
  /** The API's own sentence, shown VERBATIM. ⛔ Never replaced by a friendlier paraphrase. */
  message: string;
  fromStatus: string | null;
  attemptedStatus: string | null;
  /**
   * The SUGGESTED next statuses the refusal carried, from `suggested` or its deprecated alias
   * `allowed`. ⛔ Not a permission list — see the file header. Empty means the API sent an empty
   * one, which is a statement about SUGGESTIONS and never about what is possible.
   */
  suggested: Transition[];
  /** Whether a list was present at all, so the UI can tell "none suggested" from "none sent". */
  suggestedWasSent: boolean;
}

/**
 * PH-29 — what the GitLab hook did, on a transition that SUCCEEDED.
 *
 * 🔴 THE DEFECT THIS CLOSES WAS ON THE CLIENT, NOT THE SERVER, and the ticket blamed the wrong side.
 *   It reads: *"createIssueOnDevToDo CANNOT THROW BY CONTRACT. So with GitLab unconfigured, moving a
 *   ticket to DEV To Do silently does nothing and nobody ever learns."* The first sentence is true and
 *   the conclusion does not follow — `services/gitlab-link.ts` RETURNS `{ok:false, reason}` in the 200
 *   body, deliberately, with its own comment saying *"Returning nothing on failure would be a silent
 *   success."* The server has been reporting this all along.
 *
 * ⛔⛔ IT WAS THIS FILE THAT THREW IT AWAY. `transitionCard` ended `return { ok: true }` and dropped the
 *   whole body. So the signal existed at the API boundary and died one hop later — which is the same
 *   shape as PH-38's attribution stopping one hop short, and the same shape as `logStorageConstraint`
 *   writing a line nobody routes anywhere. ⇒ A REPORTED FAILURE THAT NO CLIENT RENDERS IS A SILENT ONE.
 *
 * ⚠️ Absent is not the same as failed. `gitlab` is only present on transitions into the status that
 *   fires the hook, so `undefined` means "this transition had nothing to do with GitLab" and must never
 *   be rendered as a problem.
 */
export interface GitlabHookOutcome {
  ok: boolean;
  /** The issue this ticket is now linked to, when there is one. */
  issueIid: string | null;
  /** True only when THIS request created it — a re-entry into the status finds the existing issue. */
  created: boolean;
  /** ⛔ The API's own sentence, shown verbatim. Present only on failure. */
  reason: string | null;
}

export type TransitionOutcome =
  | { ok: true; gitlab?: GitlabHookOutcome }
  | { ok: false; refusal: TransitionRefusal };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

export function parseRefusal(body: unknown): TransitionRefusal {
  if (!isRecord(body)) {
    return {
      message: 'The API refused the move but sent no explanation.',
      fromStatus: null,
      attemptedStatus: null,
      suggested: [],
      suggestedWasSent: false,
    };
  }

  // ⚠️ EITHER key counts as "a list was sent". `suggested` is the current name; `allowed` is the
  // deprecated alias, and a pre-PH-19 deployment sends only the latter. Checking just one would make
  // a refusal from the other server look as though it listed nothing.
  const suggestedWasSent = Array.isArray(body.suggested) || Array.isArray(body.allowed);
  return {
    message:
      readString(body, 'message') ??
      readString(body, 'error') ??
      'The API refused the move but sent no explanation.',
    fromStatus: readString(body, 'from_status'),
    attemptedStatus: readString(body, 'to_status'),
    // ⭐ Read through the SAME parser as the discovery endpoint — one place understands the
    // `{code,label,lane,colour,note}` shape and the suggested/allowed precedence, so the refusal and
    // the dropdown cannot disagree.
    suggested: suggestedWasSent ? asTransitions(body, '/tickets/:id/transition') : [],
    suggestedWasSent,
  };
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

export async function transitionCard(
  ticketId: Id,
  toStatus: string,
  options: { note?: string; signal?: AbortSignal } = {},
): Promise<TransitionOutcome> {
  const url = buildUrl(API_V1, `/tickets/${ticketId}/transition`);
  let response: Response;

  try {
    response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      // ⚠️ `toStatus`, camelCase — the API's body key. `to_status` is silently ignored by zod and the
      // request fails validation, which reads as "the board is broken" rather than "wrong key".
      body: JSON.stringify({ toStatus, ...(options.note ? { note: options.note } : {}) }),
      signal: options.signal ?? null,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw toApiError(err, url);
  }

  if (response.status === 409) {
    return { ok: false, refusal: parseRefusal(await readBody(response)) };
  }

  if (!response.ok) {
    const body = await readBody(response);
    const reason = isRecord(body) ? readString(body, 'message') : null;
    throw new ApiError({
      kind: classifyStatus(response.status),
      status: response.status,
      reason,
      url,
    });
  }

  return { ok: true, gitlab: parseGitlabHook(await readBody(response)) };
}

/**
 * PH-29 — read the hook result out of a SUCCESSFUL transition body.
 *
 * ⭐ DEFENSIVE ON EVERY FIELD, because this is an optional block on a response whose main purpose is
 *   something else. A deployment that predates the hook sends no `gitlab` key at all, and that must
 *   read as "nothing to report" rather than as a malformed response — so anything that is not a
 *   recognisable object returns `undefined`.
 *
 * ⛔ `ok` IS READ STRICTLY AS A BOOLEAN AND NOT COERCED. `Boolean(body.ok)` would turn a missing field
 *   into `false`, i.e. would invent a FAILURE out of an old server's silence — and a fabricated failure
 *   notice is worse than none, because it teaches people to ignore the notice.
 */
function parseGitlabHook(body: unknown): GitlabHookOutcome | undefined {
  if (!isRecord(body)) return undefined;
  const raw = body.gitlab;
  if (!isRecord(raw)) return undefined;
  if (typeof raw.ok !== 'boolean') return undefined;

  const issueIid = raw.issue_iid;
  return {
    ok: raw.ok,
    // ⚠️ The API sends this as a string ("1"). A number is accepted too rather than dropped, because
    //    losing the issue reference would leave a success notice with nothing to link to.
    issueIid:
      typeof issueIid === 'string' && issueIid.trim()
        ? issueIid
        : typeof issueIid === 'number'
          ? String(issueIid)
          : null,
    created: raw.created === true,
    reason: typeof raw.reason === 'string' && raw.reason.trim() ? raw.reason : null,
  };
}
